import {
  API,
  APIError,
  currentEnv,
  disableBrowserDefaultPinchToZoom,
  encodeURLString,
  ErrorHandling,
  EVENT_APP_INITIAL_LOADING_COMPLETE,
  EVENT_APP_LOADING_COMPLETE,
  EVENT_APP_TERMINATING,
  EVENT_REQUEST_APP_AVATAR_STATE,
  EVENT_REQUEST_EXIT_MONSTER_WORLD,
  executeWithRetry,
  FatalErrors,
  FEATURE_SWITCHES,
  FeatureSwitchManager,
  fetchWrapper,
  getIslandType,
  getLauncherStartupOptions,
  getLoadingTags,
  getUpdatedLauncherStartupOptions,
  HandlingMethod,
  type iFrameLauncherMessage,
  isIOSAppEnv,
  Logger,
  mocksResponse,
  type OnAppTerminatingData,
  patchFetch,
  sharedErrorHandlingCases,
  WebGL2Error,
} from "@classdojo/monsterverse-utils";
import { type BenchmarkResult, runGPUBenchmark } from "@monsterverse/gpu-benchmark";
import { IslandType } from "@monsterverse/island-types";

import { getAWSHostURLs, handleUserDataRequests } from "./AWSHostURLs";
import { handleIFrameMessages, postToApp } from "./MessageHandler";
import { type IframeCallbackMessage, type IframeCallbacks, Networking } from "./Networking";
import NetworkingWorker from "./NetworkingWorker.worker?worker";
import { fatalErrorIfOutage, OutageError } from "./Outage";
import { shim } from "./Shim";
import { SplashScreenPresentation } from "./SplashScreenPresentation";
import { handleFatalErrors } from "./UI/FatalErrorModal";
import { fatalErrorIfNoWasm, WasmError } from "./Wasm";
import { fatalErrorIfNoWebGL2 } from "./WebGL2";

let _networking: Networking | undefined;

let _networkingWorker: Worker | undefined;
/*
 * If we are seeing errors or bad placements with the
 * GPU benchmark and want to revert quickly, set this
 * true and deploy.
 */
const DISABLE_GPU_BENCHMARK = false;

async function main() {
  // Disable the default pinch to zoom behavior for browsers
  // that do not support the user-scalable meta tag. We call
  // this as early as possible to disable this before everything..
  disableBrowserDefaultPinchToZoom();

  // ===== SYSTEM INITIALIZATION =====
  // Logger setup and metrics collection
  Logger.initialize(FeatureSwitchManager, { env: currentEnv(), customEndpoint: "/logs" });
  let hasLoggedFatalError = false;
  const returnUserToApp = (reason: string, error?: Error, unexpected?: boolean) => {
    if (!hasLoggedFatalError) {
      if (error && error instanceof APIError) {
        Logger.logInfo("Returning user to app", { reason, errorMsg: error?.message ?? "NULL", error: error.toJSON() });
      } else {
        Logger.logInfo("Returning user to app", { reason, errorMsg: error?.message ?? "NULL" });
      }
      Logger.flushLogs();
      hasLoggedFatalError = true;
    }
    postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD, eventValues: [{ unexpected }] });
  };

  // ===== COMPREHENSIVE ERROR HANDLING SYSTEM =====
  // Register specific error cases with appropriate handling strategies
  ErrorHandling.initialize()
    .registerErrorCase("Old IOS clients with non fatal error in resizeIfNeeded function", {
      method: HandlingMethod.DisregardError,
      isCondition: (error) => {
        return (
          error.message === "undefined is not an object (evaluating 'pc.app.graphicsDevice.canvas')" &&
          error.stack !== undefined &&
          error.stack.includes("resizeIfNeeded")
        );
      },
    })
    .registerErrorCase("Outage", {
      method: HandlingMethod.DisregardError,
      isCondition: (error) => {
        return error instanceof OutageError;
      },
    })
    .registerErrorCase("No Wasm", {
      method: HandlingMethod.DisregardError,
      isCondition: (error) => {
        return error instanceof WasmError;
      },
    })
    .registerErrorCase("No WebGL2", {
      method: HandlingMethod.DisregardError,
      isCondition: (error) => {
        return error instanceof WebGL2Error;
      },
    })
    .registerErrorCase("Fatal error caught during loading; sending user back to app.", {
      method: HandlingMethod.HandleError,
      isCondition: () => {
        return true;
      },
      onHandle: (error?: Error) => {
        returnUserToApp("LauncherFatalErrorWhileLoading", error, true);
      },
    })
    .registerErrorCases(
      ...sharedErrorHandlingCases((reason: string, error?: Error, unexpected?: boolean) => {
        returnUserToApp(`Launcher${reason}`, error, unexpected);
      })
    );

  // ===== RUNTIME ENVIRONMENT SETUP =====
  // Apply necessary shims and patches for browser compatibility
  shim();
  patchFetch();

  // Initialize API client and metrics
  API.init("https://clients.multiplayer.classdojo.com", getLauncherStartupOptions().entityType, {
    Logger,
    ErrorHandling,
    FatalErrors,
    fetchWrapper,
  });
  const loadTags = getLoadingTags();
  Logger.incrementMetric("monsterverse.stats.launcher.open", 1, {
    platform: Logger.getPlatform(),
    ...loadTags,
  });

  // ===== DOM ELEMENT SETUP =====
  // Validate required DOM elements exist
  const loadingEl = document.getElementById("application-launcher-splash-wrapper");
  if (!loadingEl) {
    return;
  }

  const iframeEl = document.getElementById("application-iframe");
  if (!(iframeEl instanceof HTMLIFrameElement)) {
    return;
  }

  // ===== IFRAME COMMUNICATION SETUP =====
  // Establish message handling and event systems

  // First set up a callback to fire when the presentation is complete.
  // If this isn't firing reliably, we can assume the loading screen is not dismissing.
  const onPresentationComplete = (): void => {
    Logger.incrementMetric("monsterverse.stats.launcher.complete", 1, {
      platform: Logger.getPlatform(),
      ...loadTags,
    });
  };

  // Setup handler for events coming from the iframe we're building.
  const pcAppEventHandler = handleIFrameMessages(iframeEl);
  // And ensure we have a presentation to handle showing the loading progress, and progress bar.
  SplashScreenPresentation.initialize(loadingEl, pcAppEventHandler, onPresentationComplete);

  handleFatalErrors(iframeEl, pcAppEventHandler);
  handleUserDataRequests(iframeEl);
  await fatalErrorIfOutage();

  const _onChangeIsland = (islandType: "class" | "home" | "community" | "personal", islandId: string) => {
    const url = new URL(location.href);

    if (mocksResponse()) {
      url.search = getUpdatedLauncherStartupOptions(islandId);
      location.replace(url);
      return;
    }

    if (islandType !== "class") {
      url.search = getUpdatedLauncherStartupOptions(islandId);
      location.replace(url);
      return;
    }

    // call API to get updated island url for target classId
    // (startupParams might be different from current island)

    // Remove islandId type prefix
    const classId = islandId.replace(/^[a-z]+-/, "");
    executeWithRetry(
      () =>
        API.call({
          method: "get",
          path: "/api/monsterverseConfig",
          query: {
            classId,
          },
        }),
      2
    )
      .then((response) => {
        // @ts-expect-error TODO: Property config doesn't exist on response
        url.search = getUpdatedLauncherStartupOptions(islandId, response.config.island.startupParams || {});
        //Do a replace to not keep history (Android navigates "back" to close Monsterverse)
        location.replace(url);
      })
      .catch(() => {
        // fallback to try to open the island with same startup params as current island
        url.search = getUpdatedLauncherStartupOptions(islandId);
        location.replace(url);
      });
  };

  // ===== CLIENT LOADING PROCESS =====
  // Main client initialization and configuration
  const loadClient = async () => {
    // Parameter extraction and logger setup
    const { entityType, islandId, startupParams, URLdata } = getLauncherStartupOptions();
    const { spawnPoint } = URLdata ?? {};
    Logger.setEntityType(entityType === "student" ? "studentUser" : entityType);

    const selfParams = new URL(window.location.href).searchParams;
    const bundleId = selfParams.get("bundle");
    const forceWebGL1 = selfParams.get("webgl1") ?? false;

    // Start this first because the network request is async
    // and the benchmark may block the main thread slightly (though only for a few milliseconds)
    const hostURLsPromise = getAWSHostURLs(islandId ?? "default", entityType, bundleId ?? undefined);

    let benchmarkTierPromise: Promise<BenchmarkResult | undefined> | undefined;
    if (DISABLE_GPU_BENCHMARK) {
      benchmarkTierPromise = Promise.resolve(undefined);
    } else {
      try {
        benchmarkTierPromise = fetch("https://clients.multiplayer.classdojo.com/config/benchmark/thresholds.json")
          .then((response) => {
            if (response.ok) {
              return response.json();
            }
            return null;
          })
          .catch(() => {
            // Silently ignore fetch errors - we'll use default thresholds
            return null;
          })
          .then((data) => {
            const config: Parameters<typeof runGPUBenchmark>[0] = { timeout: 5000 };
            if (data && data.thresholds) {
              config.thresholds = data.thresholds;
            }
            return runGPUBenchmark(config);
          })
          .catch((e) => {
            Logger.logWarn("Failed to finish GPU benchmark", { error: e });
            return undefined;
          });
      } catch (e) {
        Logger.logWarn("Failed to start GPU benchmark", { error: e });
      }
    }

    // AWS service integration - fetch server configuration and authentication
    const [
      {
        wsUrl,
        hubUrl,
        island,
        countryCode,
        serverRegion,
        desiredServerRegion,
        editor,
        auth,
        monsterConfig,
        classYear,
        customizerUrl,
        homeGradeLevel,
      },
      benchmarkTierResult,
    ] = await Promise.all([hostURLsPromise, benchmarkTierPromise ?? Promise.resolve(undefined)]);

    const benchmarkTier = benchmarkTierResult?.tier as number | undefined;

    // Browser capability validation (must happen after AWS call for translations)
    fatalErrorIfNoWebGL2();
    fatalErrorIfNoWasm();

    // Logger context configuration
    Logger.setServerID(island.id);
    Logger.setCountryCode(countryCode);

    const islandType = IslandType[getIslandType(island.id)];
    Logger.setIslandType(islandType);
    if (classYear) {
      Logger.setClassYear(classYear);
    }

    const isHomeIsland = islandType === IslandType[IslandType.Home];

    // URL parameter parsing for client configuration
    const urlParams = new URLSearchParams(window.location.search);
    const entityId = urlParams.get("entityId");
    const sessionId = urlParams.get("sessionId");
    const ghostMode = urlParams.get("ghost") === "true";
    const perfTier = urlParams.get("perfTier");
    const skipHttpAssetCache = urlParams.get("skipHttpAssetCache");
    const miniStats = urlParams.get("miniStats");
    const disableUI = urlParams.get("disableUI");
    const noFrameLimiting = urlParams.get("noFrameLimiting");
    const showSystemStats = urlParams.get("showSystemStats");
    const useTextureAO = urlParams.get("useTextureAO");

    // Helper function to decode base64url encoded parameters
    function decodeURLString(str: string): string {
      // Replace non-url compatible chars with base64 standard chars (base64url encoding)
      // https://www.rfc-editor.org/rfc/rfc4648#page-7
      return decodeURIComponent(atob(str.replace(/-/g, "+").replace(/_/g, "/")));
    }

    function decodeQueryParams(str: string): URLSearchParams {
      try {
        return new URLSearchParams(JSON.parse(decodeURLString(str || "")));
      } catch {
        return new URLSearchParams();
      }
    }

    const startupParamsDecoded = decodeQueryParams(startupParams);
    const loadingSplashParams = {
      backgroundUrl:
        (isHomeIsland &&
          (startupParamsDecoded.get("hBg") ||
            startupParamsDecoded.get("loadingBackground") ||
            "https://static.classdojo.com/mw/loading_screen_home.jpg")) ||
        startupParamsDecoded.get("loadingBackground") ||
        "https://static.classdojo.com/mw/loading_screen_dragon.jpg",
    };

    if (sessionId) {
      Logger.setSessionId(sessionId);
    }

    // iframe URL construction with all required parameters
    const hubUrlBuilder = new URL(hubUrl);
    hubUrlBuilder.searchParams.set(
      "data",
      encodeURLString(
        JSON.stringify({
          island,
          entityType,
          countryCode,
          spawnPoint,
          ghostMode,
          monsterConfig,
          classYear,
          customizerUrl,
        })
      )
    );
    hubUrlBuilder.searchParams.set("sessionId", sessionId ?? Logger.getSessionId());
    if (forceWebGL1) {
      console.log("Running in debug webgl1 mode");
      hubUrlBuilder.searchParams.set("webgl1", "true");
    }
    if (entityId) {
      Logger.setEntityId(entityId);
      hubUrlBuilder.searchParams.set("entityId", entityId);
    }

    if (auth) {
      Logger.setAuthType(auth);
    }

    hubUrlBuilder.searchParams.set("startupParams", startupParams);

    // Optional debugging and performance parameters
    if (perfTier) {
      hubUrlBuilder.searchParams.set("perfTier", perfTier);
    }

    if (skipHttpAssetCache) {
      hubUrlBuilder.searchParams.set("skipHttpAssetCache", skipHttpAssetCache);
    }

    if (miniStats) {
      hubUrlBuilder.searchParams.set("miniStats", miniStats);
    }

    if (disableUI !== undefined && disableUI !== null) {
      hubUrlBuilder.searchParams.set("disableUI", disableUI);
    }

    if (noFrameLimiting !== undefined && noFrameLimiting !== null) {
      hubUrlBuilder.searchParams.set("noFrameLimiting", noFrameLimiting);
    }

    if (showSystemStats) {
      hubUrlBuilder.searchParams.set("showSystemStats", showSystemStats);
    }

    if (useTextureAO) {
      hubUrlBuilder.searchParams.set("useTextureAO", useTextureAO);
    }

    if (bundleId) {
      hubUrlBuilder.searchParams.set("bundle", bundleId);
    }

    if (editor) {
      hubUrlBuilder.searchParams.set("editor", "true");
    }

    hubUrlBuilder.searchParams.set("auth", auth);
    if (homeGradeLevel) {
      hubUrlBuilder.searchParams.set("homeGradeLevel", homeGradeLevel);
    }

    // Custom loading splash params
    const { backgroundUrl } = loadingSplashParams;
    if (backgroundUrl) {
      hubUrlBuilder.searchParams.set("backgroundUrl", window.btoa(backgroundUrl));
    }

    if (benchmarkTier !== undefined) {
      hubUrlBuilder.searchParams.set("benchmarkTier", benchmarkTier.toString());
    }

    // iframe loading with history management
    // Remove/re-add trick prevents browser history retention
    const parent = iframeEl.parentElement;
    iframeEl.remove();
    iframeEl.src = hubUrlBuilder.toString();
    parent?.appendChild(iframeEl);

    let usingWebsocketWorker = false;
    if (FeatureSwitchManager.isFeatureSwitchEnabled(FEATURE_SWITCHES.MONSTERVERSE_USE_WEBSOCKET_WORKER)) {
      usingWebsocketWorker = true;
      _networkingWorker = new NetworkingWorker();
      _networkingWorker.addEventListener("message", (event) => {
        if (event.data.type === "onIframeMessage") {
          const { message } = event.data;
          iframeEl.contentWindow?.postMessage(message, "*");
        } else if (event.data.type === "removeIframe") {
          iframeEl.remove();
        } else if (event.data.type === "triggerFatalError") {
          ErrorHandling.instance.triggerFatalError(event.data.error);
        } else if (event.data.type === "logPerfStats") {
          Logger.logPerfStats(event.data.stats);
        } else if (event.data.type === "logEvent") {
          Logger.logEvent(event.data.event);
        } else if (event.data.type === "logInfo") {
          Logger.logInfo(event.data.message, event.data.context);
        } else if (event.data.type === "onChangeIsland") {
          _onChangeIsland(event.data.islandType, event.data.islandId);
        }
      });

      // Now initialize Networking inside the worker
      _networkingWorker.postMessage({
        type: "init",
        wsUrl: wsUrl.toString(),
        islandId: island.id,
        countryCode,
        serverRegion,
        desiredServerRegion,
        _ENV: window._ENV || "local",
      });

      window.addEventListener("message", (ev: MessageEvent<iFrameLauncherMessage>) => {
        _networkingWorker?.postMessage({ type: "onIframeMessage", message: ev.data });
      });
    } else {
      // Networking system initialization
      const iframeCallbacks: IframeCallbacks = {
        postMessage: (message: IframeCallbackMessage) => {
          iframeEl.contentWindow?.postMessage(message, "*");
        },
        removeIFrame: () => {
          iframeEl.remove();
          location.reload();
        },
        triggerFatalError: (error: FatalErrors, hostname?: string) => {
          if (hostname) {
            ErrorHandling.instance.triggerFatalError(error, hostname);
          } else {
            ErrorHandling.instance.triggerFatalError(error);
          }
        },
        onChangeIsland: (islandType: "class" | "home" | "community" | "personal", islandId: string) => {
          _onChangeIsland(islandType, islandId);
        },
      };
      _networking = new Networking(
        window._ENV || "local",
        iframeCallbacks,
        Logger,
        wsUrl,
        island.id,
        countryCode,
        serverRegion,
        desiredServerRegion
      );
      window.addEventListener("message", (ev: MessageEvent<iFrameLauncherMessage>) => {
        _networking?.onIFrameMessage(ev.data);
      });
    }

    pcAppEventHandler.on(EVENT_APP_LOADING_COMPLETE, () => {
      if (usingWebsocketWorker) {
        _networkingWorker?.postMessage({ type: "onAppLoadingComplete" });
      } else {
        _networking?.onAppLoadingComplete();
      }
    });
    pcAppEventHandler.on(EVENT_APP_TERMINATING, (data: unknown) => {
      if (usingWebsocketWorker) {
        _networkingWorker?.postMessage({ type: "onAppTerminating", data });
      } else {
        _networking?.onAppTerminating(data as OnAppTerminatingData);
      }
    });
  };

  // ===== IOS-SPECIFIC COORDINATION =====
  // Client loading happens after the iOS host is ready
  // This ensures the host has registered the handshake messages before we try and fire them
  if (isIOSAppEnv()) {
    let appAvatarStateRegistered = false;
    let appInitialLoadingRegistered = false;
    pcAppEventHandler.onRegistration(EVENT_REQUEST_APP_AVATAR_STATE, () => {
      appAvatarStateRegistered = true;
      if (appInitialLoadingRegistered) {
        loadClient();
      }
    });
    pcAppEventHandler.onRegistration(EVENT_APP_INITIAL_LOADING_COMPLETE, () => {
      appInitialLoadingRegistered = true;
      if (appAvatarStateRegistered) {
        loadClient();
      }
    });
  } else {
    loadClient();
  }
}

main();
